import { DialogWithForm } from '@strata/react-dialog';
import { PhoneInputFormik } from '@strata/react-inputs';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { type MemberDetails, usePostMemberDataUpdateMutation } from 'store/api/scopeAssessorApi';
import { type TreatmentDetails } from 'store/api/treatmentApi';

interface UpdateMemberPhoneNumberDialogProps {
    isOpen: boolean;
    onClose: () => void;
    treatmentDetails?: TreatmentDetails;
    memberDetails?: MemberDetails;
}

export const UpdateMemberPhoneNumberDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    memberDetails = undefined,
}: UpdateMemberPhoneNumberDialogProps) => {
    const [postMemberDataUpdate] = usePostMemberDataUpdateMutation();

    return (
        <Formik
            initialValues={{
                payerId: treatmentDetails?.payerId,
                phone: memberDetails?.phone,
                secondaryPhone: memberDetails?.secondaryPhone,
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                secondaryPhone: PhoneInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={async (values, { resetForm }) => {
                if (!treatmentDetails?.payerId || !memberDetails || !values.secondaryPhone) {
                    return;
                }
                await postMemberDataUpdate({
                    payerId: treatmentDetails.payerId,
                    memberId: memberDetails.memberId,
                    firstName: memberDetails.firstName,
                    dateOfBirth: memberDetails.dateOfBirth,
                    phone: values.secondaryPhone,
                }).unwrap();

                resetForm();
                onClose();
                SuccessToast({ message: 'Member data updated successfully.' });
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Update Member Phone Number"
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update Member Phone Number"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <PhoneInputFormik id="phone" label="Member Phone" required disabled />
                            <PhoneInputFormik
                                id="secondaryPhone"
                                label="Payer Submitted Supplementary Phone Number"
                                required
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};
